/**
 * The library chatbot on `/research`. It is a practice-scoped Casey thread
 * with its own remembered conversation, not the dashboard's. Every agent
 * message carries every library source id (`metadata.research_sources`), so
 * the turn note and `tool_search.promoted` load the immigration-library MCP
 * tools. There is no magnifier: the sources are always all of them.
 */
import { Library, SquarePen } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AgentComposerBar } from "@/features/agents/components/AgentComposerBar";
import { AgentTranscript } from "@/features/agents/components/AgentTranscript";
import type { AgentScope, AgentSpec } from "@/features/agents/types";
import type { AgentMessageMetadata } from "@/features/agents/useAgentChat";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { useScopedAgentChat } from "@/features/agents/useScopedAgentChat";
import { cn } from "@/lib/utils";

import { RESEARCH_SOURCE_IDS } from "../sources";

/** The sources this chat names on every message, in library order. */
export const LIBRARY_RESEARCH_METADATA: AgentMessageMetadata = {
  research_sources: [...RESEARCH_SOURCE_IDS]
};

/** This page's thread, separate from the practice Casey the panel remembers. */
export const researchThreadKey = (practiceId: string): string =>
  `research.library.${practiceId}`;

const COLUMN = "mx-auto w-full max-w-3xl";

const ChatHeader: FC<{ onNew?: () => void }> = ({ onNew }) => (
  <header className="flex h-11 shrink-0 items-center gap-2 border-b border-line-2 px-4">
    <Library className="size-4 shrink-0 text-fg-3" />
    <h2
      className="min-w-0 flex-1 truncate text-sm font-semibold text-fg"
      data-testid="research-chat-title"
    >
      Ask about the Library
    </h2>
    {onNew ? (
      <Button
        variant="ghost"
        size="icon-xs"
        className="text-fg-3"
        aria-label="New Thread"
        onClick={onNew}
        data-testid="research-chat-new"
      >
        <SquarePen />
      </Button>
    ) : null}
  </header>
);

type LiveProps = {
  className?: string;
  scope: AgentScope;
  spec: AgentSpec | null;
};

const ResearchChatLive: FC<LiveProps> = ({ className, scope, spec }) => {
  const scoped = useScopedAgentChat({
    agent: "practice",
    scope,
    spec,
    owner: "research-library",
    threadKey: researchThreadKey(scope.id),
    messageMetadata: LIBRARY_RESEARCH_METADATA
  });
  const { chat } = scoped;

  return (
    <section
      className={cn("flex min-h-0 flex-col bg-surface", className)}
      data-testid="research-chat"
      aria-label="Ask about the Library"
    >
      <ChatHeader
        onNew={
          scoped.empty || chat.threadsLoading ? undefined : chat.startNewThread
        }
      />
      {scoped.empty ? (
        <div
          className={cn(
            COLUMN,
            "flex min-h-0 flex-1 flex-col justify-end px-4 pb-2"
          )}
        >
          <p className="text-md text-fg-2">
            Ask about decisions, statutes, manuals, and policy updates.
          </p>
        </div>
      ) : (
        <AgentTranscript
          scoped={scoped}
          className="min-h-0 flex-1"
          contentClassName={cn(COLUMN, "px-4")}
        />
      )}
      <AgentComposerBar
        scoped={scoped}
        research={false}
        showModeSelect={false}
        draftKey={`research-library:${scope.id}`}
        placeholder="Ask about the library"
        hideDisclaimer
        autoFocus={false}
        className={cn(COLUMN, "px-4 pb-1")}
      />
      <p className="shrink-0 pb-2 text-center text-sm text-fg-3">
        Answers come from the immigration library. Please verify them.
      </p>
    </section>
  );
};

type Props = {
  className?: string;
};

/**
 * Casey's composer and transcript, titled for the library. Sending opens the
 * thread in this pane (the same streaming transcript and tool cards). The
 * search box above stays put.
 */
export const ResearchChat: FC<Props> = ({ className }) => {
  const { practiceScope, specFor, loading } = useAgentSpecs();
  if (!practiceScope) {
    return (
      <section
        className={cn("flex min-h-0 flex-col bg-surface", className)}
        data-testid="research-chat"
        aria-label="Ask about the Library"
      >
        <ChatHeader />
        <div className="flex min-h-0 flex-1 flex-col justify-end px-4 pb-4">
          {loading ? (
            <Skeleton className="mx-auto h-10 w-full max-w-3xl rounded-xl" />
          ) : (
            <p className="text-center text-sm text-fg-3">
              The library assistant is unavailable.
            </p>
          )}
        </div>
      </section>
    );
  }
  return (
    <ResearchChatLive
      className={className}
      scope={practiceScope}
      spec={specFor("assistant")}
    />
  );
};
